import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Calendar, FileText, Loader2 } from "lucide-react";

interface ReportCardProps {
  title: string;
  description: string;
  category: "compliance" | "wallet" | "funding" | "partnership";
  lastGenerated?: Date;
  frequency?: "daily" | "weekly" | "monthly" | "quarterly" | "on-demand";
  onGenerate?: () => void;
  onDownload?: () => void;
  isDownloading?: boolean;
}

const categoryConfig = {
  compliance: { label: "Compliance", className: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800" },
  wallet: { label: "Wallet", className: "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800" },
  funding: { label: "Funding", className: "bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-900/20 dark:text-cyan-400 dark:border-cyan-800" },
  partnership: { label: "Partnership", className: "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800" },
};

export function ReportCard({ 
  title, 
  description, 
  category, 
  lastGenerated, 
  frequency,
  onGenerate,
  onDownload,
  isDownloading = false
}: ReportCardProps) {
  const categoryConf = categoryConfig[category];
  
  return (
    <Card data-testid={`card-report-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <CardTitle className="text-lg">{title}</CardTitle>
            <Badge variant="outline" className={`mt-2 ${categoryConf.className}`}>
              {categoryConf.label}
            </Badge>
          </div>
          <FileText className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{description}</p>
        
        {frequency && (
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              Generated {frequency === "on-demand" ? "on demand" : frequency}
            </span>
          </div>
        )}
        
        {lastGenerated && (
          <div className="text-sm text-muted-foreground">
            Last generated: {lastGenerated.toLocaleDateString()}
          </div>
        )}
        
        <div className="flex gap-2 flex-wrap">
          {onGenerate && (
            <Button 
              variant="default" 
              size="sm" 
              onClick={onGenerate}
              disabled={isDownloading}
              data-testid={`button-generate-${title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {isDownloading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate Report"
              )}
            </Button>
          )}
          {onDownload && lastGenerated && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onDownload}
              disabled={isDownloading}
              data-testid={`button-download-${title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
